<template>
  <!-- PROP-402: основная область для ответа-презентации агента. HTML показываем в песочнице
       CbsHtmlSandbox (iframe sandbox без allow-same-origin) — тот же путь, что у кастомных
       HTML-страниц платформы. -->
  <div class="cbs-presentation">
    <div class="cbs-presentation__bar">
      <feather-icon
        icon="MonitorIcon"
        size="16"
        class="mr-50"
      />
      <span class="cbs-presentation__title">Презентация</span>
      <!-- PROP-409: PDF собирает сервер (openhtmltopdf), а не печать браузера — так в файл не
           попадают браузерные колонтитулы, а лист, шрифт и подпись задаём мы. -->
      <b-button
        v-if="page"
        size="sm"
        variant="outline-primary"
        class="cbs-presentation__pdf ml-auto"
        :disabled="savingPdf"
        @click="savePdf"
      >
        <feather-icon
          icon="DownloadIcon"
          size="14"
          class="mr-50"
        />{{ savingPdf ? 'Готовлю PDF…' : 'Сохранить в PDF' }}
      </b-button>
    </div>
    <div
      v-if="pdfError"
      class="cbs-presentation__error text-danger"
    >
      {{ pdfError }}
    </div>
    <cbs-html-sandbox
      v-if="page"
      ref="sandbox"
      :key="presentation.at"
      :page="page"
      :min-height="minHeight"
      class="cbs-presentation__frame"
    />
    <!-- BUG-429: страницы живут в памяти вкладки, и ссылка на давний ответ может пережить саму
         страницу. Тогда говорим об этом прямо: показать вместо неё последнюю значило бы выдать
         один ответ за другой — тот самый дефект, из-за которого номера и появились. -->
    <div
      v-else-if="gone"
      class="cbs-presentation__empty text-muted"
    >
      Этой презентации больше нет: страницы живут только в текущей вкладке — до её перезагрузки
      и пока их не вытеснили новые. Спросите агента заново.
    </div>
    <div
      v-else
      class="cbs-presentation__empty text-muted"
    >
      Здесь появится ответ-презентация. Задайте вопрос агенту в режиме «Презентация»
      на панели справа.
    </div>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import axios from '@axios'
import useJwt from '@/cubus/jwt/useJwt'
import CbsHtmlSandbox from '@/cubus/components/html-sandbox/CbsHtmlSandbox.vue'
import { presentation, openPresentation } from '@/cubus/services/chatPresentation'

export default {
  name: 'CbsPresentation',
  components: { CbsHtmlSandbox, BButton },
  data() {
    return {
      // Реактивное общее хранилище: панель кладёт сюда html, страница перерисовывается.
      presentation,
      minHeight: 400,
      // PROP-409: идёт ли сейчас сборка PDF на сервере (кнопка на это время занята).
      savingPdf: false,
      pdfError: '',
      // BUG-429: запрошена страница, которой уже нет.
      gone: false,
    }
  },
  computed: {
    page() {
      if (this.gone || !presentation.html) return null
      // Ad-hoc страница: без доступа к данным платформы (пустые whitelist) — презентация
      // статична, а открывать ей API незачем.
      return {
        sid: 'agent-presentation',
        html: presentation.html,
        objectwhitelist: [],
        procwhitelist: [],
      }
    },
  },
  watch: {
    // BUG-429: номер страницы приходит адресом, поэтому следим за ним, а не только за монтированием
    // — переход между двумя ответами внутри одного маршрута компонент не пересоздаёт.
    '$route.query.p': {
      immediate: true,
      handler(id) { this.showRequested(id) },
    },
  },
  mounted() {
    this.updateHeight()
    window.addEventListener('resize', this.updateHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateHeight)
  },
  methods: {
    /**
     * BUG-429: показать страницу, которую попросили адресом.
     *
     * Без номера — маршрут открыт «вообще» (из меню, сразу после ответа): показываем последнюю,
     * как раньше. С номером — только её: не нашли, значит честно говорим, что её нет.
     */
    showRequested(id) {
      const key = String(id || '')
      if (!key) { this.gone = false; return }
      this.gone = !openPresentation(key)
    },
    // Статичная страница не шлёт `resize`, поэтому высоту iframe тянем под вьюпорт сами.
    updateHeight() {
      this.minHeight = Math.max(400, window.innerHeight - 130)
    },
    /**
     * PROP-409: PDF собирает СЕРВЕР, а не печать браузера.
     *
     * Печать браузера ставила свои колонтитулы (адрес, дата) и не давала управлять листом —
     * отключение там галочкой в диалоге, программно её не снять. Теперь html уходит на
     * `/api/pdf`, а обратно приходит готовый файл: лист, поля, шрифт и подпись — наши.
     *
     * Имя в подписи ставит сервер по сессии; отсюда шлём только местное время печатающего.
     */
    async savePdf() {
      if (this.savingPdf || !presentation.html) return
      this.savingPdf = true
      this.pdfError = ''
      const at = new Date().toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
      try {
        const res = await axios.post(
          '/api/pdf',
          { html: presentation.html, stamp: { brand: 'Cubus', at } },
          {
            responseType: 'blob',
            headers: { Authorization: `Bearer ${useJwt.getCubusToken()}` },
          },
        )
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        const a = document.createElement('a')
        a.href = url
        a.download = 'cubus-presentation.pdf'
        document.body.appendChild(a)
        a.click()
        a.remove()
        // Освобождаем не сразу: Safari успевает начать скачивание уже после клика.
        setTimeout(() => window.URL.revokeObjectURL(url), 10000)
      } catch (e) {
        // Отказ приходит JSON'ом, но `responseType: blob` делает его блобом — читаем текстом,
        // иначе человек увидел бы «[object Blob]» вместо причины.
        let msg = e && e.message ? e.message : 'не удалось'
        const data = e && e.response && e.response.data
        if (data && typeof data.text === 'function') {
          try {
            const parsed = JSON.parse(await data.text())
            if (parsed && parsed.error) msg = parsed.error
          } catch (ignore) { /* не JSON — оставляем сообщение axios */ }
        }
        this.pdfError = `PDF не сохранён: ${msg}`
      } finally {
        this.savingPdf = false
      }
    },
  },
}
</script>

<style scoped>
.cbs-presentation {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
.cbs-presentation__bar {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  font-weight: 600;
}
.cbs-presentation__frame {
  flex: 1 1 auto;
}
.cbs-presentation__error {
  margin-bottom: 0.5rem;
  font-size: 12px;
}
.cbs-presentation__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  min-height: 240px;
  text-align: center;
  padding: 2rem;
}
</style>
